import { type RoomProduct, type WasteMode } from "./document"
import { productInk } from "./overlay"
import { cartonsFor } from "./patterns"
import { type ProductAssignment, rollGood } from "./seams"

// Per-product order rollups across the whole takeoff (all sheets — you order
// once per product, not per sheet). Pure + vitest-covered; the UI feeds it
// computeAllRooms output.

export type RollupInput = {
  product: RoomProduct | null
  orderSf: number | null
  /** Which waste model produced orderSf — a rollup mixing computed rows and
   *  rule-of-thumb rows must SAY so, or the totals can't be defended. */
  mode?: WasteMode
}

export type RollupRow = {
  key: string // product id
  label: string
  productType: string | null
  /** The product's overlay ink — matches the canvas and the room rows. */
  color: string
  roomCount: number
  orderSf: number
  sqftPerCarton: number | null
  cartons: number | null
  /** The one waste mode behind every row of this product, or "mixed". */
  mode: WasteMode | "mixed" | null
  /** Roll goods with a cut plan: nesting totals instead of cartons. LF
   *  includes pattern-repeat allowances — it is what gets ordered. */
  lf: number | null
  rollCount: number | null
}

export type Rollups = {
  products: RollupRow[]
  unassignedCount: number
  /** Rooms WITH a product whose sheet isn't calibrated — flagged, never silently dropped. */
  unmeasuredCount: number
}

/** One product category's share of the takeoff — carpet vs tile vs LVP. */
export type TypeRollupRow = {
  key: string
  label: string
  /** The first product of the type — the ink that category reads as on the plan. */
  color: string
  productCount: number
  roomCount: number
  orderSf: number
  cartons: number | null
  lf: number | null
  rollCount: number | null
}

/**
 * The per-product rollup collapsed to product TYPE — the "how much carpet, how
 * much tile" answer the takeoff panel leads with. Cartons and rolls are summed
 * from the per-product rows rather than recomputed: cartons round per product
 * (you cannot cut one product's carton to fill another's), so summing the
 * already-rounded counts is the only honest total.
 */
export function typeRollups(products: RollupRow[]): TypeRollupRow[] {
  const byType = new Map<string, TypeRollupRow>()
  for (const row of products) {
    const key = row.productType ?? "Unspecified"
    let entry = byType.get(key)
    if (!entry) {
      entry = {
        key,
        label: key,
        color: row.color,
        productCount: 0,
        roomCount: 0,
        orderSf: 0,
        cartons: null,
        lf: null,
        rollCount: null,
      }
      byType.set(key, entry)
    }
    entry.productCount++
    entry.roomCount += row.roomCount
    entry.orderSf += row.orderSf
    if (row.cartons != null) entry.cartons = (entry.cartons ?? 0) + row.cartons
    if (row.lf != null) entry.lf = (entry.lf ?? 0) + row.lf
    if (row.rollCount != null) entry.rollCount = (entry.rollCount ?? 0) + row.rollCount
  }
  return [...byType.values()].sort((a, b) => a.label.localeCompare(b.label))
}

export function productLabel(product: RoomProduct): string {
  const label = [product.style, product.color, product.format_label]
    .filter(Boolean)
    .join(" · ")
  return label || product.id
}

export function productRollups(
  rows: RollupInput[],
  assignments: Record<string, ProductAssignment> = {}
): Rollups {
  const byId = new Map<string, RollupRow & { modes: Set<WasteMode> }>()
  let unassignedCount = 0
  let unmeasuredCount = 0

  for (const row of rows) {
    if (!row.product) {
      unassignedCount++
      continue
    }
    if (row.orderSf == null) unmeasuredCount++

    let entry = byId.get(row.product.id)
    if (!entry) {
      entry = {
        key: row.product.id,
        label: productLabel(row.product),
        productType: row.product.product_type,
        color: productInk(row.product)!,
        roomCount: 0,
        orderSf: 0,
        sqftPerCarton: row.product.sqft_per_carton,
        cartons: null,
        mode: null,
        lf: null,
        rollCount: null,
        modes: new Set<WasteMode>(),
      }
      byId.set(row.product.id, entry)
    }
    entry.roomCount++
    if (row.orderSf != null) entry.orderSf += row.orderSf
    if (row.mode) entry.modes.add(row.mode)
  }

  // Cartons round the SUMMED order (per-room ceiling would systematically
  // overbuy — you cut cartons across rooms). Roll goods carry nesting totals
  // instead — cartons would be a category error on broadloom.
  const products = [...byId.values()]
    .map(({ modes, ...entry }) => {
      const assignment = assignments[entry.key]
      const roll = rollGood({ product_type: entry.productType } as RoomProduct)
      return {
        ...entry,
        mode: modes.size > 1 ? ("mixed" as const) : (modes.values().next().value ?? null),
        cartons: roll ? null : cartonsFor(entry.orderSf, entry.sqftPerCarton),
        lf: assignment ? assignment.totalLf : null,
        rollCount: assignment ? assignment.rolls.length : null,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))

  return { products, unassignedCount, unmeasuredCount }
}
